import { memo, useId, useState } from "react";
import type { ChangeEvent, ClipboardEvent, KeyboardEvent, ReactNode, SyntheticEvent } from "react";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/utils/cn";
import type { CatalogSelectChangeEvent, CatalogSelectOption, GridSize } from "../types";

const FIELD_LABEL_CLASS = "text-xs font-medium leading-[18px] tracking-[0.018em] text-secondary-foreground";
const EMPTY_SELECT_VALUE = "__catalog_empty_value__";

const gridClass = ({ xs, md }: GridSize) => cn(
  xs === 4 && "col-span-4", xs === 6 && "col-span-6",
  xs === 8 && "col-span-8", xs === 12 && "col-span-12",
  md === 3 && "md:col-span-3", md === 4 && "md:col-span-4",
  md === 6 && "md:col-span-6", md === 9 && "md:col-span-9",
  md === 12 && "md:col-span-12",
);

type FieldShellProps = {
  id: string; label: string; required?: boolean; grid: GridSize;
  error?: boolean; helperText?: string; children: ReactNode;
};

const FieldShell = ({ id, label, required, grid, error, helperText, children }: FieldShellProps) => (
  <div className={cn("min-w-0 space-y-1", gridClass(grid))}>
    <label className={FIELD_LABEL_CLASS} htmlFor={id}>
      {label}{required ? <span className="text-primary"> *</span> : null}
    </label>
    {children}
    {helperText ? (
      <p id={`${id}-help`} className={cn("text-xs leading-4 text-muted-foreground", error && "text-destructive")} role={error ? "alert" : undefined}>
        {helperText}
      </p>
    ) : null}
  </div>
);

type CatalogFormSelectProps = {
  label: string; name: string; value: string;
  onChange: (event: CatalogSelectChangeEvent) => void;
  disabled?: boolean; placeholder?: string; required?: boolean; grid?: GridSize;
  className?: string; options: CatalogSelectOption[]; allowEmpty?: boolean;
  testId?: string; optionTestIdPrefix?: string; error?: boolean; helperText?: string;
};

export const CatalogFormSelect = memo(function CatalogFormSelect({
  label, name, value, onChange, disabled, placeholder, required = false,
  grid = { xs: 12, md: 6 }, className, options, allowEmpty = false,
  testId, optionTestIdPrefix, error = false, helperText,
}: CatalogFormSelectProps) {
  const id = `${testId || `ai-catalog-form-select-${name}`}-${useId()}`;
  const normalizedOptions = allowEmpty
    ? [{ label: placeholder || "", value: EMPTY_SELECT_VALUE }, ...options]
    : options;

  return (
    <FieldShell id={id} label={label} required={required} grid={grid} error={error} helperText={helperText}>
      <Select name={name} value={value} onValueChange={(nextValue) => onChange({ target: { name, value: nextValue === EMPTY_SELECT_VALUE ? "" : nextValue } })} disabled={disabled}>
        <SelectTrigger id={id} data-testid={testId || `ai-catalog-form-select-${name}`} aria-invalid={error || undefined} aria-describedby={helperText ? `${id}-help` : undefined} className={cn("h-9 w-full rounded-lg border-[var(--input-border)] bg-[var(--background)] px-3 text-sm shadow-xs hover:bg-[var(--accent)] dark:bg-[var(--background)] dark:hover:bg-[var(--accent)]", className)}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {normalizedOptions.map((option, index) => (
            <SelectItem data-testid={optionTestIdPrefix ? `${optionTestIdPrefix}-${index}` : undefined} key={String(option.value)} value={String(option.value)}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldShell>
  );
});

type CatalogFormTextFieldProps = {
  label: string; name: string; value: string | number;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean; placeholder?: string; required?: boolean; type?: string;
  inputProps?: Record<string, string | number>; grid?: GridSize; className?: string;
  suffix?: ReactNode; testId?: string; error?: boolean; helperText?: string;
};

const CONTROL_KEYS = new Set(["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"]);
const isDecimalNumberField = (inputProps?: CatalogFormTextFieldProps["inputProps"]) => {
  const step = inputProps?.step;
  if (step === undefined) return false;
  if (step === "any") return true;
  const numericStep = Number(step);
  return Number.isFinite(numericStep) && !Number.isInteger(numericStep);
};
const isValidNumericText = (value: string, allowDecimal: boolean) => value === "" || (allowDecimal ? /^\d*\.?\d*$/.test(value) : /^\d*$/.test(value));
const getNextInputValue = (input: HTMLInputElement, insertedText: string) => {
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? input.value.length;
  return input.value.slice(0, start) + insertedText + input.value.slice(end);
};

export const CatalogFormTextField = memo(function CatalogFormTextField({
  label, name, value, onChange, disabled, placeholder, required = false,
  type = "text", inputProps, grid = { xs: 12, md: 6 }, className,
  suffix, testId, error = false, helperText,
}: CatalogFormTextFieldProps) {
  const id = `${testId || `ai-catalog-form-input-${name}`}-${useId()}`;
  const isNumberField = type === "number";
  const allowDecimal = isDecimalNumberField(inputProps);
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!isNumberField || event.ctrlKey || event.metaKey || event.altKey || CONTROL_KEYS.has(event.key) || event.key.length !== 1) return;
    if (!isValidNumericText(getNextInputValue(event.currentTarget, event.key), allowDecimal)) event.preventDefault();
  };
  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    if (isNumberField && !isValidNumericText(getNextInputValue(event.currentTarget, event.clipboardData.getData("text")), allowDecimal)) event.preventDefault();
  };
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!isNumberField || isValidNumericText(event.target.value, allowDecimal)) onChange(event);
  };

  return (
    <FieldShell id={id} label={label} required={required} grid={grid} error={error} helperText={helperText}>
      <div className="relative">
        <Input id={id} data-testid={testId || `ai-catalog-form-input-${name}`} name={name} value={value} onChange={handleChange} type={isNumberField ? "text" : type} inputMode={isNumberField ? (allowDecimal ? "decimal" : "numeric") : undefined} pattern={isNumberField ? (allowDecimal ? "[0-9]*[.]?[0-9]*" : "[0-9]*") : undefined} onKeyDown={handleKeyDown} onPaste={handlePaste} disabled={disabled} placeholder={placeholder} required={required} aria-invalid={error || undefined} aria-describedby={helperText ? `${id}-help` : undefined} className={cn("rounded-lg border-[var(--input-border)] bg-[var(--background)]", suffix && "pe-12", className)} {...inputProps} />
        {suffix ? <span className="pointer-events-none absolute inset-y-0 flex items-center text-xs text-muted-foreground [inset-inline-end:0.75rem]">{suffix}</span> : null}
      </div>
    </FieldShell>
  );
});

type CatalogChipInputProps = {
  label: string; value: string[];
  onChange: (event: SyntheticEvent, newValue: string[]) => void;
  placeholder: string; grid?: GridSize; testId?: string; error?: boolean; helperText?: string; disabled?: boolean;
};

export const CatalogChipInput = memo(function CatalogChipInput({
  label, value, onChange, placeholder, grid = { xs: 12, md: 12 }, testId,
  error = false, helperText, disabled,
}: CatalogChipInputProps) {
  const id = `${testId || "ai-catalog-form-chip-input"}-${useId()}`;
  const [draft, setDraft] = useState("");
  const commitDraft = (event: SyntheticEvent) => {
    const next = draft.trim();
    if (!next) return;
    onChange(event, [...value, next]);
    setDraft("");
  };

  return (
    <FieldShell id={id} label={label} grid={grid} error={error} helperText={helperText}>
      <div className={cn("flex min-h-9 flex-wrap items-center gap-1 rounded-lg border border-[var(--input-border)] bg-[var(--background)] px-2 py-1 shadow-xs focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50", error && "border-destructive ring-destructive/20", disabled && "cursor-not-allowed opacity-50")}>
        {value.map((item, index) => (
          <span key={`${item}-${index}`} className="inline-flex h-6 items-center gap-1 rounded-md bg-muted px-2 text-xs text-foreground">
            {item}
            <button type="button" disabled={disabled} aria-label={`Remove ${item}`} className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed" onClick={(event) => onChange(event, value.filter((_, itemIndex) => itemIndex !== index))}>
              <X className="size-3" />
            </button>
          </span>
        ))}
        <input id={id} data-testid={testId || "ai-catalog-form-chip-input"} value={draft} disabled={disabled} placeholder={value.length ? "" : placeholder} className="h-7 min-w-32 flex-1 bg-transparent px-1 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50" onChange={(event) => setDraft(event.target.value)} onBlur={commitDraft} onKeyDown={(event) => { if (event.key === "Enter" || event.key === ",") { event.preventDefault(); commitDraft(event); } }} aria-invalid={error || undefined} aria-describedby={helperText ? `${id}-help` : undefined} />
      </div>
    </FieldShell>
  );
});
